import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Eye, Factory, Filter } from 'lucide-react'

import type {
    ListTeaPurchasesParams,
    TeaPurchase,
} from '@/lib/api/tea-purchase-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { RefreshButton } from '@/components/common/refresh-button'
import { MarkProcessedDialog } from '@/components/tea-purchases/mark-processed-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useTeaPurchasesList } from '@/hooks/use-tea-purchase-query'
import { formatQty, formatVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

type ProcessingFilter = 'all' | 'pending' | 'processed'

interface ProcessingSearch {
    filter?: ProcessingFilter
    page?: number
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/tea-purchases/processing',
)({
    component: TeaPurchaseProcessingPage,
    validateSearch: (search: Record<string, unknown>): ProcessingSearch => {
        const out: ProcessingSearch = {}
        if (
            search.filter === 'all' ||
            search.filter === 'pending' ||
            search.filter === 'processed'
        ) {
            out.filter = search.filter
        }
        const p =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(p) && p > 1) out.page = p
        return out
    },
})

function TeaPurchaseProcessingPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')

    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/tea-purchases/processing' })
    const setSearch = (next: ProcessingSearch) => {
        void navigate({ search: next, replace: false })
    }

    const filter: ProcessingFilter = search.filter ?? 'pending'
    const page = search.page ?? 1
    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const filterActive = !!search.filter || (search.page ?? 1) > 1
    const clearFilter = () => setSearch({})

    const [openFilter, setOpenFilter] = useState(false)
    const [targetTeaPurchase, setTargetTeaPurchase] =
        useState<TeaPurchase | null>(null)

    const params = useMemo<ListTeaPurchasesParams>(
        () => ({
            page,
            per_page: 50,
            tea_state: 'fresh',
            ...(filter === 'pending' ? { processed: false } : {}),
            ...(filter === 'processed' ? { processed: true } : {}),
        }),
        [page, filter],
    )
    const { data, isLoading, isFetching, refetch } = useTeaPurchasesList(params)
    const items = data?.data ?? []
    const total = data?.meta?.total ?? 0

    const renderFilters = () => (
        <div>
            <Label className="mb-1 block">
                {m.tea_purchase_section_production()}
            </Label>
            <Select
                value={filter}
                onValueChange={(v) =>
                    setSearch({
                        ...search,
                        filter: v as ProcessingFilter,
                        page: undefined,
                    })
                }
            >
                <SelectTrigger className="w-full md:w-48">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="pending">
                        {m.tea_purchase_not_processed()}
                    </SelectItem>
                    <SelectItem value="processed">
                        {m.tea_purchase_processed()}
                    </SelectItem>
                    <SelectItem value="all">
                        {m.tea_purchase_filter_state()}
                    </SelectItem>
                </SelectContent>
            </Select>
        </div>
    )

    return (
        <div className="space-y-4 px-1 py-2 md:p-6">
            {/* Header */}
            <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-2">
                <div className="flex items-center gap-2">
                    <Factory className="size-5 md:size-6 text-primary" />
                    <h1 className="text-lg md:text-2xl font-semibold">
                        {m.tea_purchase_processing_title()}
                    </h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <RefreshButton
                        onClick={() => void refetch()}
                        loading={isFetching}
                    />
                    <ClearFilterButton
                        active={filterActive}
                        onClear={clearFilter}
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        className="md:hidden"
                        onClick={() => setOpenFilter(true)}
                    >
                        <Filter className="size-4 mr-1" />
                        {m.common_filter()}
                    </Button>
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                {m.tea_purchase_processing_subtitle()}
            </p>

            {/* Desktop filter inline */}
            <div className="hidden md:flex md:items-end md:gap-3">
                {renderFilters()}
                <div className="ml-auto text-sm text-muted-foreground">
                    {m.tea_purchase_total({ count: total })}
                </div>
            </div>

            {/* Mobile total */}
            <div className="md:hidden text-right text-sm text-muted-foreground">
                {m.tea_purchase_total({ count: total })}
            </div>

            {/* Mobile filter dialog */}
            <Dialog open={openFilter} onOpenChange={setOpenFilter}>
                <DialogContent className="max-w-none w-screen h-[100svh] rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6 flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{m.common_filter()}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                                clearFilter()
                                setOpenFilter(false)
                            }}
                        >
                            {m.common_clear_filter()}
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={() => setOpenFilter(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.tea_purchase_code()}</TableHead>
                            <TableHead>{m.tea_purchase_grower()}</TableHead>
                            <TableHead className="text-right">
                                {m.tea_purchase_quantity()}
                            </TableHead>
                            <TableHead>
                                {m.tea_purchase_purchase_date()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.tea_purchase_dry_quantity()}
                            </TableHead>
                            <TableHead>
                                {m.tea_purchase_processed_at()}
                            </TableHead>
                            <TableHead className="text-right" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.tea_purchase_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((n) => (
                                <TableRow key={n.id}>
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/tea-purchases/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {n.grower_name || '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(n.quantity)}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {formatVN(n.purchase_date)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {n.dry_quantity != null ? (
                                            formatQty(n.dry_quantity)
                                        ) : (
                                            <Badge variant="outline">
                                                {m.tea_purchase_not_processed()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {n.processed_at
                                            ? formatVN(n.processed_at)
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {canWrite && (
                                                <Button
                                                    size="sm"
                                                    variant={
                                                        n.dry_quantity != null
                                                            ? 'outline'
                                                            : 'default'
                                                    }
                                                    onClick={() =>
                                                        setTargetTeaPurchase(n)
                                                    }
                                                >
                                                    <Factory className="size-4 mr-1" />
                                                    {n.dry_quantity != null
                                                        ? m.common_edit()
                                                        : m.tea_purchase_action_mark_processed()}
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                asChild
                                            >
                                                <Link
                                                    to="/tea-purchases/$id"
                                                    params={{ id: n.id }}
                                                >
                                                    <Eye className="size-4" />
                                                </Link>
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {page}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <MarkProcessedDialog
                open={!!targetTeaPurchase}
                onOpenChange={(v) => !v && setTargetTeaPurchase(null)}
                teaPurchase={targetTeaPurchase}
            />
        </div>
    )
}
